/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { animateHeight } from './animate.js';

export const TABS_SHOW_DURATION = 500;
export const TABS_HIDE_DURATION = 500;

export const Tabs = function (tabsEl) {
    if (!tabsEl) return null;

    const DEFAULT_HEADER_HEIGHT = 20;

    // A plain object, not `this`. This is called as a factory, and in the
    // old non-strict script `this` was `window` — so every method was
    // leaking onto the global object and the factory handed back `window`
    // itself. A module is strict, where the same call gives `undefined`.
    const self = {};
    const containerEl = tabsEl.parentElement;

    const headerEl = document.getElementById('header');
    const footerEl = document.querySelector('.warnings--static');

    const labelsEls = Array.from(tabsEl.querySelectorAll('.label'));
    const bodiesEls = Array.from(tabsEl.querySelectorAll('.body'));

    // Utility to compute element outerHeight (including margins)
    const outerHeightWithMargin = (el) => {
        const cs = window.getComputedStyle(el);
        const marginTop = Number.parseFloat(cs.marginTop) || 0;
        const marginBottom = Number.parseFloat(cs.marginBottom) || 0;
        return el.offsetHeight + marginTop + marginBottom;
    };

    const updateTabSize = (labelEl, bodyEl, currentHeight, targetHeight) => {
        bodyEl.style.height = `${currentHeight}px`;

        if (targetHeight > 0) {
            // Last child that is not a heading.
            const bodyChildren = Array.from(bodyEl.children);
            const content = [...bodyChildren]
                .filter((c) => !/^H[1-6]$/.test(c.tagName))
                .pop();

            if (content) {
                let siblingsHeight = 0;
                bodyChildren.forEach((c) => {
                    if (c !== content)
                        siblingsHeight += outerHeightWithMargin(c);
                });
                const contentHeight = Math.max(
                    0,
                    targetHeight - siblingsHeight
                );
                // mimic .outerHeight(px)
                const cs = window.getComputedStyle(content);
                const border =
                    (Number.parseFloat(cs.borderTopWidth) || 0) +
                    (Number.parseFloat(cs.borderBottomWidth) || 0) +
                    (Number.parseFloat(cs.paddingTop) || 0) +
                    (Number.parseFloat(cs.paddingBottom) || 0);
                content.style.height = `${Math.max(0, contentHeight - border)}px`;
            }
        }
    };

    const toggleTab = (
        labelEl,
        bodyEl,
        currentHeight,
        targetHeight,
        activate
    ) => {
        if (labelEl) labelEl.classList.toggle('active', activate);
        if (bodyEl) bodyEl.classList.toggle('active', activate);
        if (bodyEl) updateTabSize(labelEl, bodyEl, currentHeight, targetHeight);
    };

    let containerCloseHandler = null;

    self.openTab = function (event) {
        if (event !== undefined) event.preventDefault();

        const oldLabel = tabsEl.querySelector('.label.active');
        const oldBody = tabsEl.querySelector('.body.active');

        // the clicked anchor
        const a = event?.currentTarget || this;
        // resolve label
        const newLabel = a.closest('.label');

        // resolve body by last #fragment in href
        let newBody = null;
        try {
            const href = a.getAttribute('href') || '';
            const m = /#[^#]+$/.exec(href);
            if (m) newBody = document.querySelector(m[0]);
        } catch {
            // The href's fragment is author-supplied and need not be a
            // valid selector. No match is the same as no tab.
        }

        const headerHeight = headerEl
            ? headerEl.offsetHeight
            : DEFAULT_HEADER_HEIGHT;
        const footerHeight = footerEl ? footerEl.offsetHeight : 0;

        const labelsBarHeight = labelsEls.length
            ? labelsEls[0].offsetHeight
            : 0;

        const newHeight =
            (containerEl ? containerEl.clientHeight : 0) -
            labelsBarHeight -
            headerHeight -
            footerHeight;

        const isCollapsed = !oldLabel;

        // Close tabs when clicking somewhere else (except "comments" link in articles)
        if (!containerCloseHandler) {
            containerCloseHandler = (ev) => {
                const t = ev.target;
                const clickInsideTabs = tabsEl.contains(t);
                const isCommentsLink =
                    t instanceof Element &&
                    (t.matches('a.comments') || t.closest?.('a.comments'));
                if (!clickInsideTabs && !isCommentsLink) {
                    self.closeTab();
                }
            };
        }
        containerEl.addEventListener('click', containerCloseHandler);
        containerEl.addEventListener('touchstart', containerCloseHandler, {
            passive: true
        });

        if (isCollapsed) {
            // Open all tabs and activate the clicked tab
            if (newLabel && newBody) {
                toggleTab(newLabel, newBody, 0, newHeight, true);
                animateHeight(
                    newBody,
                    newHeight,
                    TABS_SHOW_DURATION,
                    () => {
                        if (oldLabel && oldBody)
                            toggleTab(oldLabel, oldBody, 0, 0, false);
                    }
                );
            }
        } else {
            const clickedSame = oldLabel === newLabel;
            if (clickedSame) {
                self.closeTab();
            } else if (newLabel && newBody) {
                toggleTab(oldLabel, oldBody, 0, 0, false);
                toggleTab(newLabel, newBody, newHeight, newHeight, true);
            }
        }
    };

    self.closeTab = (event) => {
        if (event !== undefined) event.preventDefault();

        containerEl.removeEventListener('click', containerCloseHandler);
        containerEl.removeEventListener('touchstart', containerCloseHandler);

        const label = tabsEl.querySelector('.label.active');
        const body = tabsEl.querySelector('.body.active');

        if (body) {
            animateHeight(body, 0, TABS_HIDE_DURATION, () => {
                toggleTab(label, body, 0, 0, false);
            });
        }
    };

    // wire up header links
    tabsEl.querySelectorAll('.label a').forEach((a) => {
        a.addEventListener('click', self.openTab);
        a.addEventListener('touchstart', self.openTab, { passive: false });
    });

    // on resize, adjust active body height
    const onResize = () => {
        // An element with no layout box is not on screen.
        const visible = !!(
            tabsEl.offsetParent || tabsEl.getClientRects().length
        );
        if (!visible) return;

        const label = tabsEl.querySelector('.label.active');
        const body = tabsEl.querySelector('.body.active');
        if (!label || !body) return;

        const headerHeight = headerEl
            ? headerEl.offsetHeight
            : DEFAULT_HEADER_HEIGHT;
        const footerHeight = footerEl ? footerEl.offsetHeight : 0;
        const labelsBarHeight = labelsEls.length
            ? labelsEls[0].offsetHeight
            : 0;

        const h =
            (containerEl ? containerEl.clientHeight : 0) -
            labelsBarHeight -
            headerHeight -
            footerHeight;

        updateTabSize(label, body, h, h);
    };

    window.addEventListener('resize', onResize);

    return self;
};

export default Tabs;
